import { Injectable } from '@angular/core';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import * as moment from 'moment';
import 'moment-timezone';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

type DealerTimeZoneToMomentMap = { [id: number]: string };

@Injectable()
export class DealerTimezoneService {
  dealerTimeZoneData$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
    // selectAuthenticatedEmployee emits null before the auth slice hydrates; wait for a real
    // employee so the switchMap below never dereferences rooftopId on null.
    filter((employee) => !!employee?.rooftopId),
    switchMap((employee) => {
      return this.systemControlStateService.getSystemControlValues(
        ['CL_TIMEZONE_ID', 'CL_DAYLIGHT_SAVINGS_ENABLED'],
        employee.rooftopId
      );
    }),
    map((sysControls) => {
      return {
        dealerTz: parseInt(sysControls['CL_TIMEZONE_ID']?.value || '0', 10) || null,
        daylightSavingsEnabled: sysControls['CL_DAYLIGHT_SAVINGS_ENABLED']?.value === '1',
      };
    })
  );

  dealerTimeZoneToMomentMap: DealerTimeZoneToMomentMap = {
    1: 'America/St_Johns', //'Newfoundland Standard Time'
    2: 'America/Halifax', //'Atlantic Standard Time'
    3: 'America/Atikokan', //'Eastern Standard Time'
    4: 'America/Toronto', //'US Eastern Standard Time'
    5: 'America/Regina', //'Central Standard Time'
    6: 'America/Winnipeg', //'Canada Central Standard Time'
    7: 'America/Edmonton', //'US Mountain Standard Time'
    8: 'America/Creston', //'Mountain Standard Time'
    9: 'America/Vancouver', // 'Pacific Standard Time'
    10: 'America/Anchorage', //'Alaskan Standard Time'
  };

  constructor(
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService
  ) {}

  convertUtcToDealerTimeZoneAsIso(utcDateTime: string | Date): Observable<string> {
    return this.dealerTimeZoneData$.pipe(
      map(({ dealerTz, daylightSavingsEnabled }) => {
        if (!dealerTz) return '';
        const mappedTimeZoneId = this.getMappedDealerTimezoneId(dealerTz, daylightSavingsEnabled);
        const timezone = this.dealerTimeZoneToMomentMap[mappedTimeZoneId];
        if (!timezone) return '';
        return moment.utc(utcDateTime).tz(timezone).format();
      })
    );
  }

  getMappedDealerTimezoneId(dealerTimezoneId: number, daylightSavingsEnabled: boolean): number {
    //Core removed timezones specific to DST/Non-DST and will always pass in the non-dst timezone
    //So we now map to the correct timezone based on the daylightSavingsEnabled (SCT).
    // 1: 'America/St_Johns', //'Newfoundland Standard Time'
    // 2: 'America/Halifax', //'Atlantic Standard Time'
    // 3: 'America/Atikokan', //'Eastern Standard Time - NO DST'
    // 4: 'America/Toronto', //'US Eastern Standard Time'
    // 5: 'America/Regina', //'Central Standard Time - NO DST'
    // 6: 'America/Winnipeg', //'Canada Central Standard Time'
    // 7: 'America/Edmonton', //'US Mountain Standard Time'
    // 8: 'America/Creston', //'Mountain Standard Time - NO DST'
    // 9: 'America/Vancouver', // 'Pacific Standard Time'
    // 10: 'America/Anchorage', //'Alaskan Standard Time'
    switch (dealerTimezoneId) {
      case 3:
      case 4:
        return daylightSavingsEnabled ? 4 : 3;
      case 5:
      case 6:
        return daylightSavingsEnabled ? 6 : 5;
      case 7:
      case 8:
        return daylightSavingsEnabled ? 7 : 8;
      default:
        return dealerTimezoneId;
    }
  }

  convertBrowserTimeToDealerTime(date: string, dealerTz: number, daylightSavingsEnabled: boolean) {
    const mappedTimeZoneId = this.getMappedDealerTimezoneId(dealerTz, daylightSavingsEnabled);

    const timezone = this.dealerTimeZoneToMomentMap[mappedTimeZoneId];
    const browserMoment = moment(date);
    const wallClock = browserMoment.format('YYYY-MM-DD HH:mm');

    // Reinterpret as dealer timezone
    const dealerMoment = moment.tz(wallClock, 'YYYY-MM-DD HH:mm', timezone);

    const utcToStore = dealerMoment.clone().utc().format();
    return utcToStore;
  }
}
